import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, ChevronLeft, ChevronRight, CheckCircle2, AlertCircle, FileText, Loader2, Phone, SlidersHorizontal } from 'lucide-react';
import { Button } from '../ui/Button.jsx';
import { Input } from '../ui/Input.jsx';
import { campaignApi } from '../../api/outboundCampaign.js';
import { fetchAllOwnedIvrAvatars } from '../../api/ivrAgents.js';
import { tenantsApi } from '../../api/tenants.js';
import { EndpointPickerDialog } from './EndpointPickerDialog.jsx';
import { TrunkCallerIdFields, describeTrunkCallerId } from './TrunkCallerIdFields.jsx';
import { api } from '../../api/client.js';

/**
 * CreateCampaignWizard v2 — claude skill 智能分析名單
 *
 * Steps:
 *   1. 基本資訊（名稱 + AI 客服）
 *   2. 上傳檔案（沒 mapping UI — 後端用 claude 智能分析）
 *   3. 撥打時段
 *   4. 撥打規則
 *   5. 撥打順序
 *   6. 開場白（待 claude 分析後可看到變數）
 *   7. 確認 + 啟動
 *
 * 流程：
 *   - 用戶按「下一步」（Step 2 → 3）時不分析，等 Step 7 提交才上傳
 *   - 提交：(1) 建活動 → (2) 上傳檔案到 /api/outbound-import/start → (3) 等 claude
 *           解析完成 (preview_ready) → (4) confirm → (5) 啟動 campaign
 */

const STEPS = [
    { id: 1, labelKey: 'wizard.steps.basic' },
    { id: 2, labelKey: 'wizard.steps.import' },
    { id: 3, labelKey: 'wizard.steps.window' },
    { id: 4, labelKey: 'wizard.steps.rules' },
    { id: 5, labelKey: 'wizard.steps.greeting' },
    { id: 6, labelKey: 'wizard.steps.confirm' },
];
// 撥打順序固定用「尚未撥打優先」(callOrder='pending_first'),不開放 UI 選擇

export function CreateCampaignWizard({ open, onClose, hostId, onCreated }) {
    const { t } = useTranslation('outbound');
    const [step, setStep] = useState(1);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);

    // Step 1
    const [name, setName] = useState('');
    const [avatarType, setAvatarType] = useState('ivr');  // 'ivr' = AI 客服 | 'survey' = 問卷
    const [avatarId, setAvatarId] = useState('');
    // API 活動樣板：勾選後 scheduler 不自動外撥，只當 External Dial API 的設定樣板。預設不勾。
    const [isApiTemplate, setIsApiTemplate] = useState(false);
    const [avatars, setAvatars] = useState([]);

    // Step 2
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    // 外撥流程文件（選填）— 後端 import-outbound-list skill 讀 FLOW_DOC_PATH，
    // 依文件裡多個活動/分類設計產出對應 greetingTemplate(s)
    const flowDocInputRef = useRef(null);
    const [flowDoc, setFlowDoc] = useState(null);

    // Step 3
    const [daysOfWeek, setDaysOfWeek] = useState([1, 2, 3, 4, 5]);
    const [startHour, setStartHour] = useState(9);
    const [endHour, setEndHour] = useState(21);
    const [startAtLocal, setStartAtLocal] = useState(() => {
        const d = new Date();
        d.setMinutes(0, 0, 0);
        d.setHours(d.getHours() + 1);
        const tzOffset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
    });
    // 「立即外撥」按鈕的暫存值 — 點下後存原本的 startAt，可以再切回去
    const [savedStartAt, setSavedStartAt] = useState(null);
    const isImmediate = savedStartAt !== null;

    // Step 4
    const [dialTimeoutSec, setDialTimeoutSec] = useState(25);
    const [retryAfterMin, setRetryAfterMin] = useState(240);
    const [maxAttempts, setMaxAttempts] = useState(2);
    const [phonesPerContact, setPhonesPerContact] = useState(1);
    // 外撥分機：tenant 的 ipPhones + 勾選的 ext（預設全勾）。選幾支 = 並發數。
    const [ipPhones, setIpPhones] = useState([]);
    const [selectedExts, setSelectedExts] = useState([]);   // ext 字串陣列
    const [endpointDialogOpen, setEndpointDialogOpen] = useState(false);
    // SIP Trunk 模式（pbx.mode='trunk'）：沒有分機可選，改讓用戶設「併發線路數」，
    // 上限 = trunk.channels（購買席次，一通道=一席）。0 = 分機模式。
    const [trunkChannels, setTrunkChannels] = useState(0);
    const [trunkConcurrency, setTrunkConcurrency] = useState(2);
    // SIP Trunk 外撥顯號 / 前置碼（活動層；空 = tenant.trunk 預設）
    const [trunkInfo, setTrunkInfo] = useState(null);      // { callerIdMode, callerId, dids[] }
    const [trunkCallerId, setTrunkCallerId] = useState('');
    const [trunkDialPrefix, setTrunkDialPrefix] = useState('');
    // 外播中允許 AI 轉接真人（活動層開關，預設關；電銷「有興趣轉業務」用）+ 目標分機（空=沿用角色/租戶設定）
    const [allowTransfer, setAllowTransfer] = useState(false);
    const [transferExt, setTransferExt] = useState('');

    // 撥打順序固定「尚未撥打優先」(沒 UI step,直接 send 給後端)
    const callOrder = 'pending_first';

    // Step 5 — 開場白
    // 🔴 中文稱謂在姓名後（「王小明先生您好」）；{{title}}{{name}} 會唸成「先生王小明」（顯榮 0819 客訴實案）
    const [greetingTemplate, setGreetingTemplate] = useState(() => '{{name}}{{title}}' + t('wizard.greetingDefaultText'));

    // Progress（提交時用）
    const [progress, setProgress] = useState({ phase: 'idle', log: [] });

    useEffect(() => {
        if (!open || !hostId) return;
        // tenant + 子帳號 scope（只列被指派的 avatar）
        fetchAllOwnedIvrAvatars({ hostId }).then((list) => {
            setAvatars((list || []).filter(a => a.enabled !== false));
        }).catch(() => {});
        // 載入 tenant 的外撥分機清單 → 預設全勾；SIP Trunk 模式改抓通道數當併發上限
        tenantsApi.get(hostId).then((res) => {
            const tn = res?.tenant;
            if (tn?.pbx?.mode === 'trunk') {
                const ch = Math.max(1, Number(tn?.trunk?.channels) || 1);
                setTrunkChannels(ch);
                setTrunkConcurrency(ch);
                setTrunkInfo({
                    callerIdMode: tn?.trunk?.callerIdMode || 'pbx',
                    callerId: tn?.trunk?.callerId || '',
                    dids: Array.isArray(tn?.trunk?.dids) ? tn.trunk.dids.map(String).filter(Boolean) : [],
                });
                setIpPhones([]); setSelectedExts([]);
                return;
            }
            const phones = (tn?.ipPhones || []).filter(p => p && p.ext != null);
            setIpPhones(phones);
            setSelectedExts(phones.map(p => String(p.ext)));
        }).catch(() => { setIpPhones([]); setSelectedExts([]); });
    }, [open, hostId]);

    if (!open) return null;

    const handleFile = (f) => {
        if (!f) return;
        setFile(f);
        setError(null);
    };

    const validate = () => {
        if (step === 1) {
            if (!name.trim()) return t('wizard.err.name');
            if (!avatarId) return t('wizard.err.agent');
        }
        // step 2（名單+流程文件）全部選填 — 建好後可從 detail 頁再上傳
        if (step === 3) {
            if (daysOfWeek.length === 0) return t('wizard.err.day');
            if (startHour >= endHour) return t('wizard.err.time');
        }
        return null;
    };

    const next = () => {
        const err = validate();
        if (err) { setError(err); return; }
        setError(null);
        if (step < STEPS.length) setStep(step + 1);
    };
    const prev = () => { setError(null); setStep(Math.max(1, step - 1)); };

    /**
     * Fire-and-forget 模式：
     *   1. 建 campaign (instant)
     *   2. 上傳檔案到 ivr-builder-api（autoCommit + autoStart 設 true）
     *   3. 立刻關閉 wizard → 後續解析 / 匯入 / 啟動全在背景跑
     *   4. 用戶從 OutboundPage / detail 頁看進度（campaign card 顯示「處理中」+ importJobId）
     */
    const submit = async () => {
        setCreating(true);
        setError(null);
        try {
            setProgress({ phase: 'creating_campaign', log: [t('wizard.progress.creating')] });
            const startAtMs = new Date(startAtLocal).getTime();
            const created = await campaignApi.create(hostId, {
                name: name.trim(),
                avatarId,
                campaignType: avatarType,   // 'ivr' | 'survey' — 卡片 / 詳情頁分類顯示
                startAt: startAtMs,
                windowConfig: { daysOfWeek, startHour, endHour, timezone: 'Asia/Taipei' },
                retryConfig: { retryAfterMin, maxAttempts, phonesPerContact, dialTimeoutSec },
                callOrder,
                // 選的分機 = 並發數來源（後端 scheduler 讀 endpoints.length）。空 = 不限 = 用 tenant 全部
                // SIP Trunk 模式：沒有分機，endpoints 留空、併發走 concurrency（上限 = trunk.channels）
                endpoints: trunkChannels > 0 ? [] : selectedExts,
                concurrency: trunkChannels > 0 ? Math.min(Math.max(1, trunkConcurrency), trunkChannels) : undefined,
                isApiTemplate,   // 勾選 → scheduler 不自動外撥，只當 API 樣板
                // 外播中允許轉真人（預設關）+ 目標分機（只在開啟時送）
                allowTransfer,
                transferExt: allowTransfer ? transferExt.trim() : '',
                greetingTemplate: greetingTemplate.trim() || null,
                // SIP Trunk 顯號 / 前置碼（分機模式不送）
                ...(trunkChannels > 0 ? { callerId: trunkCallerId.trim(), dialPrefix: trunkDialPrefix.trim() } : {}),
            });
            const campaignId = created.campaign.id;
            // 帳號分鐘數已用完：照樣建立（客戶可能先建好、等加值）→ 關閉後由父層跳站內視窗提醒活動會先暫停

            // 名單 CSV 或 流程文件 任一有給就跑 import skill
            //   - 有名單 → 解析 contacts（+ 有 flowDoc 則依分類產 greetingTemplate）
            //   - 只有 flowDoc（常見於 API 樣板，無名單）→ 只產 greetingTemplates 寫回 campaign
            if (file || flowDoc) {
                setProgress((p) => ({ phase: 'uploading', log: [...p.log, t('wizard.progress.uploading')] }));
                const fd = new FormData();
                fd.append('hostId', hostId);
                fd.append('campaignId', campaignId);
                if (file) fd.append('file', file);
                // 外撥流程文件（選填）→ skill 讀 FLOW_DOC_PATH，依分類設計產 greetingTemplate(s)
                if (flowDoc) fd.append('flowDoc', flowDoc);
                fd.append('autoCommit', 'true');
                // API 樣板不自動外撥 → 匯完不啟動（scheduler 也會 skip，雙保險）
                fd.append('autoStart', isApiTemplate ? 'false' : 'true');
                await api('POST', '/ivr-builder/api/outbound-import/start', { body: fd });
            }

            // 立刻關閉 — 後續 AI 解析 / 匯入 / 啟動全在背景跑
            // 用戶看 OutboundPage / detail 頁 importJobId 進度
            onCreated?.(created.campaign, { quotaExhausted: !!created.quotaExhausted });
            onClose();
        } catch (e) {
            setError(t('wizard.err.createFail', { msg: e.message }));
            setProgress((p) => ({ phase: 'error', log: [...(p?.log || []), `❌ ${e.message}`] }));
        } finally {
            setCreating(false);
        }
    };

    const isSubmitting = creating;

    return (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center px-4">
            <div className="bg-cream-50 rounded-2xl shadow-[0_20px_60px_-20px_rgba(28,26,23,0.3)] w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200 bg-white">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400">
                            new outbound campaign · tenant {hostId}
                        </div>
                        <h2 className="heading-display text-xl font-semibold text-ink-900 mt-0.5">{t('wizard.header.title')}</h2>
                    </div>
                    <button onClick={onClose} disabled={isSubmitting} className="size-9 inline-flex items-center justify-center text-ink-500 hover:bg-cream-100 rounded-md transition disabled:opacity-30">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-3 border-b border-cream-200 bg-cream-50/50">
                    <div className="flex items-center gap-1 text-[11px] font-mono">
                        {STEPS.map((s, i) => (
                            <div key={s.id} className="flex items-center gap-1">
                                <span className={[
                                    'inline-flex items-center justify-center size-5 rounded-full text-[10px]',
                                    s.id === step ? 'bg-accent-500 text-white font-semibold' :
                                    s.id < step ? 'bg-accent-100 text-accent-700' : 'bg-cream-200 text-ink-400'
                                ].join(' ')}>{s.id}</span>
                                <span className={s.id === step ? 'text-ink-900' : 'text-ink-400'}>{t(s.labelKey)}</span>
                                {i < STEPS.length - 1 && <span className="text-ink-300 mx-1">›</span>}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {error && (
                        <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-danger-100/40 border border-danger-500/30 rounded-md text-sm text-danger-500">
                            <AlertCircle size={14} /> {error}
                        </div>
                    )}

                    {step === 1 && (
                        <div className="space-y-4">
                            <Input label={t('wizard.s1.name')} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('wizard.s1.namePlaceholder')} />

                            {/* 類型切換 — AI 客服 / 問卷 */}
                            <div>
                                <div className="text-xs text-ink-700 mb-1.5">{t('wizard.s1.type')}</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { v: 'ivr', label: t('wizard.type.ivr'), sub: t('wizard.type.ivrSub') },
                                        { v: 'survey', label: t('wizard.type.survey'), sub: t('wizard.type.surveySub') },
                                    ].map((opt) => {
                                        const active = avatarType === opt.v;
                                        return (
                                            <button key={opt.v} type="button"
                                                onClick={() => { setAvatarType(opt.v); setAvatarId(''); }}
                                                className={[
                                                    'text-left rounded-md border px-3 py-2.5 transition',
                                                    active
                                                        ? 'border-accent-500 bg-accent-50/60 ring-1 ring-accent-300'
                                                        : 'border-cream-300 bg-white hover:border-accent-300',
                                                ].join(' ')}>
                                                <div className={['text-sm font-medium', active ? 'text-accent-700' : 'text-ink-900'].join(' ')}>{opt.label}</div>
                                                <div className="text-[10px] text-ink-400 mt-0.5">{opt.sub}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <div className="text-xs text-ink-700 mb-1.5">
                                    {avatarType === 'survey' ? t('wizard.s1.surveyAgent') : t('wizard.s1.ivrAgent')}
                                </div>
                                {(() => {
                                    const filtered = avatars.filter((a) => avatarType === 'survey'
                                        ? (a.newMode === 'survey' || a.surveyMode === true)
                                        : (a.newMode !== 'survey' && a.surveyMode !== true));
                                    if (filtered.length === 0) {
                                        return (
                                            <div className="text-[11px] text-ink-400 italic px-3 py-2 border border-cream-300 rounded-md bg-cream-50">
                                                {avatarType === 'survey' ? t('wizard.s1.noAgentSurvey') : t('wizard.s1.noAgentIvr')}
                                            </div>
                                        );
                                    }
                                    return (
                                        <>
                                            <select value={avatarId} onChange={(e) => setAvatarId(e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-cream-300 rounded-md bg-white text-ink-900 focus:outline-none focus:border-accent-500">
                                                <option value="">{t('wizard.s1.pickAvatar')}</option>
                                                {filtered.map((a) => (<option key={a.avatarId} value={a.avatarId}>{a.name}</option>))}
                                            </select>
                                            <div className="text-[11px] text-ink-400 mt-1.5">
                                                {avatarType === 'survey'
                                                    ? t('wizard.s1.surveyHint')
                                                    : t('wizard.s1.ivrHint')}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            {/* API 活動樣板：勾了之後排程不自動外撥，只給 External Dial API 當設定樣板 */}
                            <label className="flex items-start gap-2.5 px-3 py-2.5 rounded-md border border-cream-300 bg-cream-50/60 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isApiTemplate}
                                    onChange={(e) => setIsApiTemplate(e.target.checked)}
                                    className="mt-0.5 accent-accent-500"
                                />
                                <span>
                                    <span className="text-[13px] text-ink-900">{t('wizard.s1.apiTemplate')}</span>
                                    <span className="block text-[11px] text-ink-400 mt-0.5">{t('wizard.s1.apiTemplateHint')}</span>
                                </span>
                            </label>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-5">
                            {/* 名單 CSV */}
                            <div>
                                <div className="text-xs font-medium text-ink-900 mb-2">{t('wizard.s2.csv')} <span className="text-danger-500">*</span></div>
                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    className="border-2 border-dashed border-cream-300 rounded-xl p-6 text-center cursor-pointer hover:border-accent-400 hover:bg-cream-100/50 transition"
                                >
                                    <Upload size={32} strokeWidth={1.2} className="mx-auto text-cream-400 mb-2" />
                                    <div className="text-sm text-ink-700">{t('wizard.s2.pickCsv')}</div>
                                    <div className="text-[11px] text-ink-400 mt-1">{t('wizard.s2.csvHint')}</div>
                                    <input ref={fileInputRef} type="file"
                                        accept=".csv,.xlsx,.xls,.txt"
                                        hidden onChange={(e) => handleFile(e.target.files?.[0])} />
                                </div>
                                {file && (
                                    <div className="mt-2 border border-cream-200 rounded-md bg-white px-3 py-2 flex items-center gap-2">
                                        <FileText size={14} className="text-accent-700" />
                                        <span className="text-sm text-ink-900 truncate flex-1">{file.name}</span>
                                        <span className="text-[11px] text-ink-400">{(file.size / 1024).toFixed(1)} KB</span>
                                        <button onClick={(e) => { e.stopPropagation(); setFile(null); }}
                                            className="text-ink-400 hover:text-danger-500 ml-1"><X size={14} /></button>
                                    </div>
                                )}
                            </div>

                            {/* 外撥流程文件（選填）— 給後端 skill 依分類設計產 greetingTemplate */}
                            <div>
                                <div className="text-xs font-medium text-ink-900 mb-2">
                                    {t('wizard.s2.flowDoc')} <span className="text-[10px] text-ink-400 font-normal">{t('wizard.s2.optional')}</span>
                                </div>
                                <div
                                    onClick={() => flowDocInputRef.current?.click()}
                                    className="border-2 border-dashed border-cream-300 rounded-xl p-4 text-center cursor-pointer hover:border-accent-400 hover:bg-cream-100/50 transition"
                                >
                                    <FileText size={24} strokeWidth={1.2} className="mx-auto text-cream-400 mb-1.5" />
                                    <div className="text-[13px] text-ink-700">{t('wizard.s2.pickFlowDoc')}</div>
                                    <div className="text-[11px] text-ink-400 mt-1">{t('wizard.s2.flowDocHint')}</div>
                                    <input ref={flowDocInputRef} type="file"
                                        accept=".docx,.pdf,.md,.txt"
                                        hidden onChange={(e) => { setFlowDoc(e.target.files?.[0] || null); setError(null); }} />
                                </div>
                                {flowDoc && (
                                    <div className="mt-2 border border-cream-200 rounded-md bg-white px-3 py-2 flex items-center gap-2">
                                        <FileText size={14} className="text-accent-700" />
                                        <span className="text-sm text-ink-900 truncate flex-1">{flowDoc.name}</span>
                                        <span className="text-[11px] text-ink-400">{(flowDoc.size / 1024).toFixed(1)} KB</span>
                                        <button onClick={(e) => { e.stopPropagation(); setFlowDoc(null); }}
                                            className="text-ink-400 hover:text-danger-500 ml-1"><X size={14} /></button>
                                    </div>
                                )}
                            </div>

                            <div className="text-[11px] text-ink-500 px-3 py-2.5 bg-cream-100/60 rounded-md leading-relaxed">
                                💡 {t('wizard.s2.tipLine1')}<br/>
                                {t('wizard.s2.tipLine2')}<br/>
                                {avatarType === 'survey'
                                    ? t('wizard.s2.tipSurvey')
                                    : t('wizard.s2.tipIvr', { name: '{{name}}', title: '{{title}}' })}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4">
                            <div>
                                <div className="text-xs text-ink-700 mb-1.5">{t('wizard.s3.days')}</div>
                                <div className="flex gap-1">
                                    {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((dk, i) => {
                                        const d = t('wizard.dayShort.' + dk);
                                        const on = daysOfWeek.includes(i);
                                        return (
                                            <button key={i} onClick={() => setDaysOfWeek(arr =>
                                                on ? arr.filter(x => x !== i) : [...arr, i].sort()
                                            )} className={[
                                                'size-9 rounded-md text-sm font-medium transition border',
                                                on ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-ink-700 border-cream-300'
                                            ].join(' ')}>{d}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <NumberField label={t('wizard.s3.startHour')} value={startHour} onChange={setStartHour} min={0} max={23} suffix=":00" />
                                <NumberField label={t('wizard.s3.endHour')} value={endHour} onChange={setEndHour} min={1} max={24} suffix=":00" />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <div className="text-xs text-ink-700">{t('wizard.s3.startAt')}</div>
                                    <button type="button"
                                        onClick={() => {
                                            if (isImmediate) {
                                                // 還原成原本的排程時間
                                                setStartAtLocal(savedStartAt);
                                                setSavedStartAt(null);
                                            } else {
                                                // 切成現在 — 把原值存起來
                                                setSavedStartAt(startAtLocal);
                                                const now = new Date();
                                                const tz = now.getTimezoneOffset() * 60000;
                                                setStartAtLocal(new Date(now - tz).toISOString().slice(0, 16));
                                            }
                                        }}
                                        className={`text-[11px] px-2 py-0.5 rounded border ${isImmediate
                                            ? 'border-cream-400 text-ink-700 hover:bg-cream-100'
                                            : 'border-accent-500/40 text-accent-700 hover:bg-accent-50'}`}>
                                        {isImmediate ? t('wizard.s3.restoreSchedule') : t('wizard.s3.immediate')}
                                    </button>
                                </div>
                                <input type="datetime-local" value={startAtLocal} onChange={(e) => {
                                    setStartAtLocal(e.target.value);
                                    setSavedStartAt(null);   // 用戶手動改過 → 不再算「立即」狀態
                                }}
                                    className="w-full px-3 py-2 text-sm border border-cream-300 rounded-md bg-white focus:outline-none focus:border-accent-500" />
                                <div className="text-[11px] text-ink-400 mt-1">
                                    {isImmediate
                                        ? t('wizard.s3.immediateOn')
                                        : t('wizard.s3.immediateOff')}
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-4">
                            <NumberField label={t('wizard.s4.dialTimeout')} value={dialTimeoutSec} onChange={setDialTimeoutSec} min={10} max={60} suffix={t('detail.suffix.sec')} hint={t('wizard.s4.dialTimeoutHint')} />
                            <NumberField label={t('wizard.s4.retryAfter')} value={retryAfterMin} onChange={setRetryAfterMin} min={1} max={1440} suffix={t('detail.suffix.min')} />
                            <NumberField label={t('wizard.s4.maxAttempts')} value={maxAttempts} onChange={setMaxAttempts} min={0} max={5} suffix={t('detail.suffix.times')} hint={t('wizard.s4.maxAttemptsHint')} />
                            <NumberField label={t('wizard.s4.phonesPerContact')} value={phonesPerContact} onChange={setPhonesPerContact} min={1} max={10} />

                            {/* SIP Trunk 模式 — 沒有分機，直接設併發線路數（上限 = trunk 通道數） */}
                            {trunkChannels > 0 ? (
                                <>
                                    <NumberField
                                        label={t('wizard.s4.trunkConcurrency')}
                                        value={trunkConcurrency}
                                        onChange={setTrunkConcurrency}
                                        min={1}
                                        max={trunkChannels}
                                        suffix={t('detail.suffix.calls')}
                                        hint={t('wizard.s4.trunkConcurrencyHint', { n: trunkChannels })}
                                    />
                                    {/* Trunk 沒有「選分機」決定顯號 → 這裡選外顯號碼 / 前置碼 */}
                                    <TrunkCallerIdFields trunkInfo={trunkInfo}
                                        callerId={trunkCallerId} onCallerId={setTrunkCallerId}
                                        dialPrefix={trunkDialPrefix} onDialPrefix={setTrunkDialPrefix} />
                                </>
                            ) : (
                            <div>
                                <div className="text-xs text-ink-700 mb-1.5">{t('wizard.s4.endpoints')}</div>
                                <div className="flex items-center justify-between gap-3 px-3.5 py-3 rounded-lg border border-cream-300 bg-white">
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <span className="size-8 inline-flex items-center justify-center rounded-lg bg-accent-50 text-accent-600 shrink-0">
                                            <Phone size={15} />
                                        </span>
                                        <div className="min-w-0">
                                            <div className="text-[13px] text-ink-900 font-medium">
                                                {ipPhones.length === 0
                                                    ? t('wizard.s4.endpointsEmpty')
                                                    : (selectedExts.length === 0 || selectedExts.length === ipPhones.length)
                                                        ? t('wizard.s4.endpointsSummaryAll', { n: ipPhones.length })
                                                        : t('wizard.s4.endpointsSummarySome', { n: selectedExts.length })}
                                            </div>
                                            {ipPhones.length > 0 && selectedExts.length > 0 && selectedExts.length < ipPhones.length && (
                                                <div className="text-[11px] text-ink-400 font-mono truncate mt-0.5">
                                                    {selectedExts.join('、')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {ipPhones.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setEndpointDialogOpen(true)}
                                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cream-300 text-[12px] font-medium text-ink-700 bg-cream-50 hover:bg-cream-100 hover:border-cream-400 transition"
                                        >
                                            <SlidersHorizontal size={13} />
                                            {t('wizard.s4.endpointsBtn')}
                                        </button>
                                    )}
                                </div>
                                <div className="text-[11px] text-ink-400 mt-1.5">
                                    {t('wizard.s4.endpointsHint', { n: selectedExts.length || ipPhones.length })}
                                </div>
                            </div>
                            )}

                            {/* 外播中允許轉真人（活動層開關，預設關）：分機模式 / SIP Trunk 都走既有轉接路徑 */}
                            {avatarType !== 'survey' && (
                                <div className="rounded-md border border-cream-300 bg-cream-50/60">
                                    <label className="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={allowTransfer}
                                            onChange={(e) => setAllowTransfer(e.target.checked)}
                                            className="mt-0.5 accent-accent-500"
                                        />
                                        <span>
                                            <span className="text-[13px] text-ink-900">{t('wizard.s4.allowTransfer')}</span>
                                            <span className="block text-[11px] text-ink-400 mt-0.5">{t('wizard.s4.allowTransferHint')}</span>
                                        </span>
                                    </label>
                                    {allowTransfer && (
                                        <div className="px-3 pb-3 pl-9">
                                            <div className="text-xs text-ink-700 mb-1">{t('wizard.s4.transferExt')}</div>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={transferExt}
                                                onChange={(e) => setTransferExt(e.target.value.replace(/[^0-9*#]/g, ''))}
                                                placeholder={t('wizard.s4.transferExtPh')}
                                                className="w-48 px-3 py-1.5 text-sm border border-cream-300 rounded-md bg-white font-mono focus:outline-none focus:border-accent-500"
                                            />
                                            <div className="text-[11px] text-ink-400 mt-1">
                                                {trunkChannels > 0 ? t('wizard.s4.transferExtHintTrunk') : t('wizard.s4.transferExtHint')}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {step === 5 && (
                        <div className="space-y-3">
                            {avatarType === 'survey' ? (
                                <div className="px-3 py-3 bg-cream-100/60 rounded-md text-[13px] text-ink-700 leading-relaxed">
                                    📝 {t('wizard.s5.surveyNote')}
                                </div>
                            ) : (
                                <div>
                                    <div className="text-xs text-ink-700 mb-1.5">{t('wizard.s5.greetingTpl')}</div>
                                    <textarea value={greetingTemplate} onChange={(e) => setGreetingTemplate(e.target.value)} rows={4}
                                        className="w-full px-3 py-2 text-sm border border-cream-300 rounded-md bg-white resize-none focus:outline-none focus:border-accent-500" />
                                    <div className="text-[11px] text-ink-400 mt-1.5">
                                        {t('wizard.s5.varsHintLine1', { vars: '{{name}} {{title}}' })}<br/>
                                        {t('wizard.s5.varsHintLine2', { examples: '{{category}} {{apply_date}} {{welfare_id}}' })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 6 && (
                        <div className="space-y-3 text-sm">
                            <SummaryRow label={t('wizard.s6.name')} value={name} />
                            <SummaryRow label={t('wizard.s6.type')} value={avatarType === 'survey' ? t('wizard.type.survey') : t('wizard.type.ivr')} />
                            {isApiTemplate && <SummaryRow label={t('wizard.s6.apiTemplate')} value={t('wizard.s6.apiTemplateVal')} />}
                            <SummaryRow label={avatarType === 'survey' ? t('wizard.s6.agentSurvey') : t('wizard.s6.agentIvr')} value={avatars.find(a => a.avatarId === avatarId)?.name || avatarId} />
                            <SummaryRow label={t('wizard.s6.file')} value={file?.name || '—'} />
                            <SummaryRow label={t('wizard.s6.days')} value={['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].filter((_, i) => daysOfWeek.includes(i)).map(dk => t('dayWeekPrefix', { d: t('wizard.dayShort.' + dk) })).join('、')} />
                            <SummaryRow label={t('wizard.s6.window')} value={`${String(startHour).padStart(2, '0')}:00 - ${String(endHour).padStart(2, '0')}:00`} />
                            <SummaryRow label={t('wizard.s6.startAt')} value={startAtLocal.replace('T', ' ')} />
                            <SummaryRow label={t('wizard.s6.ringRetry')} value={t('wizard.s6.ringRetryVal', { ring: dialTimeoutSec, retry: retryAfterMin, max: maxAttempts })} />
                            {avatarType !== 'survey' && (
                                <SummaryRow label={t('wizard.s6.transfer')} value={
                                    allowTransfer
                                        ? (transferExt.trim() ? t('wizard.s6.transferOnExt', { ext: transferExt.trim() }) : t('wizard.s6.transferOn'))
                                        : t('wizard.s6.transferOff')
                                } />
                            )}
                            <SummaryRow label={t('wizard.s6.endpoints')} value={
                                trunkChannels > 0
                                    ? t('wizard.s6.endpointsTrunkVal', { n: Math.min(Math.max(1, trunkConcurrency), trunkChannels) })
                                    : selectedExts.length === 0 || selectedExts.length === ipPhones.length
                                        ? t('wizard.s6.endpointsAllVal', { n: ipPhones.length })
                                        : t('wizard.s6.endpointsVal', { n: selectedExts.length, exts: selectedExts.join('、') })
                            } />
                            {trunkChannels > 0 && (
                                <SummaryRow label={t('trunkCid.label')} value={
                                    describeTrunkCallerId(t, trunkInfo, trunkCallerId.trim()) + (trunkDialPrefix.trim() ? t('trunkCid.prefixTag', { p: trunkDialPrefix.trim() }) : '')
                                } />
                            )}

                            {/* 提交進度 */}
                            {isSubmitting && (
                                <div className="mt-4 px-3 py-2 bg-cream-100/60 rounded-md">
                                    <div className="flex items-center gap-2 text-sm text-ink-900 mb-2">
                                        <Loader2 size={14} className="animate-spin text-accent-500" />
                                        <span>{t('wizard.s6.processing')}</span>
                                    </div>
                                    <div className="text-[11px] font-mono text-ink-500 space-y-0.5 max-h-32 overflow-y-auto">
                                        {progress.log.map((line, i) => <div key={i}>{line}</div>)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-cream-200 bg-white flex items-center justify-between">
                    <Button variant="ghost" onClick={prev} disabled={step === 1 || isSubmitting}>
                        <ChevronLeft size={14} /> {t('wizard.nav.prev')}
                    </Button>
                    <div className="text-[11px] text-ink-400">{step} / {STEPS.length}</div>
                    {step < STEPS.length ? (
                        <Button variant="accent" onClick={next}>{t('wizard.nav.next')} <ChevronRight size={14} /></Button>
                    ) : (
                        <Button variant="accent" onClick={submit} disabled={isSubmitting}>
                            <CheckCircle2 size={14} /> {isSubmitting ? t('wizard.nav.submitting') : t('wizard.nav.submit')}
                        </Button>
                    )}
                </div>
            </div>

            <EndpointPickerDialog
                open={endpointDialogOpen}
                onClose={() => setEndpointDialogOpen(false)}
                ipPhones={ipPhones}
                value={selectedExts}
                onConfirm={setSelectedExts}
            />
        </div>
    );
}

function NumberField({ label, value, onChange, min, max, suffix, hint }) {
    // 用 local string state 允許中途清空 / 部分輸入（同 OutboundCampaignDetailPage.EditNumRow 的修法）
    const [text, setText] = useState(String(value ?? ''));
    useEffect(() => { setText(String(value ?? '')); }, [value]);
    return (
        <div>
            <div className="text-xs text-ink-700 mb-1.5">{label}</div>
            <div className="flex items-center gap-2">
                <input type="number" value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        if (e.target.value === '') return;
                        const n = Number(e.target.value);
                        if (Number.isFinite(n)) onChange(n);
                    }}
                    onBlur={(e) => {
                        let n = Number(e.target.value);
                        if (!Number.isFinite(n)) n = (min ?? 0);
                        if (min != null) n = Math.max(min, n);
                        if (max != null) n = Math.min(max, n);
                        setText(String(n));
                        onChange(n);
                    }}
                    min={min} max={max}
                    className="w-24 px-2 py-1.5 text-sm border border-cream-300 rounded bg-white focus:outline-none focus:border-accent-500 tabular-nums" />
                {suffix && <span className="text-xs text-ink-500">{suffix}</span>}
            </div>
            {hint && <div className="text-[11px] text-ink-400 mt-1">{hint}</div>}
        </div>
    );
}

function SummaryRow({ label, value }) {
    return (
        <div className="flex items-start gap-3 py-1.5 border-b border-cream-100">
            <div className="text-xs text-ink-400 w-20 shrink-0">{label}</div>
            <div className="text-sm text-ink-900 flex-1">{value || '—'}</div>
        </div>
    );
}
